import {RangeType} from '../../../components/shared/calendar/types';
import {AutoCompleteItemValue} from '../../../components/shared/nextgen/selector/types';
import {VisibleDays} from '../../../features/report/types';
import {TimeSheetGroupType} from '../../../features/schedule/types';
import {GetTimeReportSubmittedHours, TimeReportItem} from '../../../features/time-report/types';

const DEFAULT_PROPERTY_FILTER = {
    selectedEmployees: [],
    selectedProjects: [],
    selectedProjectTypes: [],
    showAbsences: true,
};

const ALLOWED_RANGE_TYPES = [
    RangeType.CurrentMonth,
    RangeType.PreviousMonth,
    RangeType.ThisWeek,
    RangeType.LastWeek,
    RangeType.Custom,
];

const aggregateTimeReportItems = (items: TimeReportItem[]): TimeReportItem[] => {
    return items.reduce<TimeReportItem[]>((accum, item) => {
        const existing = accum.find((value) =>
            value.id === item.id
            && value.projectId === item.projectId
            && value.clientId === item.clientId
            && value.timeshiftType === item.timeshiftType
        );

        if (!existing) {
            accum.push({ ...item });
            return accum;
        }

        existing.workHours += item.workHours;
        existing.approvedHours += item.approvedHours;
        existing.billedHours += item.billedHours;
        existing.overtimes += item.overtimes;
        existing.absenceHours += item.absenceHours;
        existing.plannedHours += item.plannedHours;
        existing.timeBankHours = (existing.timeBankHours ?? 0) + (item.timeBankHours ?? 0);

        return accum;
    }, []);
};

const getDetailedGroupKey = (item: TimeReportItem, groupBy: TimeSheetGroupType): string => {
    return groupBy === TimeSheetGroupType.PROJECTVIEW
        ? `${item.projectId}|${item.employeeName}|${item.date}|${item.timeshiftType}`
        : `${item.employeeName}|${item.date}`;
};

const filterDetailedTimeReportItems = (
    items: TimeReportItem[],
    groupBy: TimeSheetGroupType,
    visibleDays: VisibleDays
): TimeReportItem[] => {
    if (visibleDays === VisibleDays.All) {
        return items;
    }

    const dayBuckets = items.reduce<Map<string, { submittedHours: number; plannedHours: number }>>((acc, item) => {
        const key = getDetailedGroupKey(item, groupBy);
        const current = acc.get(key) ?? {
            submittedHours: 0,
            plannedHours: item.plannedHours
        };
        current.submittedHours += GetTimeReportSubmittedHours(item);
        acc.set(key, current);
        return acc;
    }, new Map());

    return items.filter((item) => {
        const bucket = dayBuckets.get(getDetailedGroupKey(item, groupBy));

        if (!bucket) {
            return false;
        }

        if (visibleDays === VisibleDays.Incorrect) {
            return bucket.submittedHours !== bucket.plannedHours;
        }

        return true;
    });
};

const sortTimeReportItems = (
    items: TimeReportItem[],
    groupBy: TimeSheetGroupType,
    detailedByDay: boolean
): TimeReportItem[] => {
    return [...items].sort((left, right) => {
        if (groupBy === TimeSheetGroupType.PROJECTVIEW) {
            if (left.timeshiftType !== right.timeshiftType) {
                return left.timeshiftType - right.timeshiftType;
            }

            const projectCompare = left.projectName.localeCompare(right.projectName, undefined, { sensitivity: 'base' });
            if (projectCompare !== 0) {
                return projectCompare;
            }

            const employeeCompare = left.employeeName.localeCompare(right.employeeName, undefined, { sensitivity: 'base' });
            if (employeeCompare !== 0) {
                return employeeCompare;
            }

            return left.date.localeCompare(right.date);
        }

        const employeeCompare = left.employeeName.localeCompare(right.employeeName, undefined, { sensitivity: 'base' });
        if (employeeCompare !== 0) {
            return employeeCompare;
        }

        if (detailedByDay) {
            const dateCompare = left.date.localeCompare(right.date);
            if (dateCompare !== 0) {
                return dateCompare;
            }
        }

        if (left.timeshiftType !== right.timeshiftType) {
            return left.timeshiftType - right.timeshiftType;
        }

        const projectCompare = left.projectName.localeCompare(right.projectName, undefined, { sensitivity: 'base' });
        if (projectCompare !== 0) {
            return projectCompare;
        }

        return left.clientName.localeCompare(right.clientName, undefined, { sensitivity: 'base' });
    });
};

type TimeReportPropertyFilterLike = {
    selectedEmployees?: AutoCompleteItemValue[];
    selectedProjects?: AutoCompleteItemValue[];
    selectedProjectTypes?: AutoCompleteItemValue[];
    selectedShiftTypes?: AutoCompleteItemValue[];
    selectedAbsenceTypes?: AutoCompleteItemValue[];
    selectedStatuses?: AutoCompleteItemValue[];
    showAbsences: boolean;
};

const hasSelectedTimeReportEntities = (propertyFilter: TimeReportPropertyFilterLike): boolean => {
    return (propertyFilter.selectedEmployees?.length ?? 0) > 0
        || (propertyFilter.selectedProjects?.length ?? 0) > 0
        || (propertyFilter.selectedProjectTypes?.length ?? 0) > 0
        || (propertyFilter.selectedShiftTypes?.length ?? 0) > 0
        || (propertyFilter.selectedAbsenceTypes?.length ?? 0) > 0
        || (propertyFilter.selectedStatuses?.length ?? 0) > 0
        || !propertyFilter.showAbsences;
};

export {
    aggregateTimeReportItems,
    ALLOWED_RANGE_TYPES,
    DEFAULT_PROPERTY_FILTER,
    filterDetailedTimeReportItems,
    hasSelectedTimeReportEntities,
    sortTimeReportItems,
};
